<script setup>
import {computed, ref} from "vue";
import {createCSV, download} from "@/helpers/file";
import AutoTaggingCustomValue from "@/components/startups/autoTagging/AutoTaggingCustomValue.vue";
import SortableColumnHeader from "@/components/ui/SortableColumnHeader.vue";
import VClamp from "vue-clamp";
import {getNumberWithNoun} from "../../../helpers/string";
import AutoTaggingUrl from "@/components/startups/autoTagging/AutoTaggingUrl.vue";

const emit = defineEmits(['apply']);

const showModal = ref(false);
const formData = ref([]);
const columns = ref([]);
const mainFieldData = ref(null);
const dependentFieldData = ref(null);
const initColumns = ref([]);

const sort = ref({
    field: '',
    dir: ''
});

const taggedCount = computed(() => {
    return formData.value.filter(row => row.tagged_category?.length).length;
});

const noUrlCount = computed(() => {
    return formData.value.filter(row => !row.url).length;
});

const mainOptions = computed(() => {
    return mainFieldData.value?.options.map(item => item.value) || [];
});

const selectedAll = computed({
    get: () => {
        return formData.value.every(row => row.selected);
    },
    set: (value) => {
        formData.value.forEach(row => {
            row.selected = value;
        });
    }
});

const csvData = computed(() => {
    let importData = formData.value.filter(item => item.selected).map(item => {
        const newItem = {};

        columns.value.forEach(column => {
            if (column.name === 'tagged_category') {
                newItem[mainFieldData.value.name] = item[column.name];
            } else if (column.name === 'tagged_sub_category') {
                newItem[dependentFieldData.value.name] = item[column.name];
            } else {
                newItem[column.name] = item[column.name];
            }
        });

        return newItem;
    });

    return createCSV(importData);
});

function downloadFile() {
    download(csvData.value, 'startup.csv');
}

function createColumns() {
    columns.value = [];

    initColumns.value.forEach(columnName => {
        if (columnName === 'score') {
            columns.value.push({
                label: 'Confidence Score',
                name: 'score'
            })
        } else if (columnName === 'tagged_category') {
            columns.value.push({
                label: mainFieldData.value.name,
                name: columnName
            })
        } else if (columnName === 'tagged_sub_category') {
            columns.value.push({
                label: dependentFieldData.value.name,
                name: columnName
            })
        } else if (columnName === 'tagged_category_raw' || columnName === 'tagged_sub_category_raw' || columnName === 'startup_id') {
            return;
        } else {
            columns.value.push({
                label: columnName,
                name: columnName
            });
        }
    });
}

function changeSort(newSort) {
    sort.value = newSort;

    if (sort.value.field) {
        formData.value.sort((a, b) => {
            if (a[sort.value.field] < b[sort.value.field]) {
                return sort.value.dir === 'asc' ? -1 : 1;
            }
            if (a[sort.value.field] > b[sort.value.field]) {
                return sort.value.dir === 'asc' ? 1 : -1;
            }

            return 0;
        });
    }
}

function getDependentOptions(row) {
    const mainOption = mainFieldData.value.options.find(item => item.value === row['tagged_category']);

    if (!mainOption) {
        return [];
    }

    const dependentOptions = dependentFieldData.value.options.filter(item => item.depends_on === mainOption.id);

    return dependentOptions.map(item => item.value);
}

function mainOptionChanged(rowIndex) {
    formData.value[rowIndex]['tagged_sub_category'] = '';

    // Clear invalid AI values if exist. They are not needed anymore since main field was changed manually
    formData.value[rowIndex]['tagged_category_raw'] = '';
    formData.value[rowIndex]['tagged_sub_category_raw'] = '';
}

async function apply() {
    showModal.value = false;
    emit('apply', csvData.value);
}

function open(data) {
    initColumns.value = data['columns'];

    formData.value = data['data'].map(item => ({
        selected: true,
        ...item
    }));

    mainFieldData.value = data['main_field_data'];
    dependentFieldData.value = data['dependent_field_data'];
    createColumns();

    showModal.value = true;
}

defineExpose({
    open
});
</script>

<template>
    <b-modal v-model="showModal" modal-class="modal-w-xxxxl modal-auto-tagging" :title="`Tagged Startups (${taggedCount} of ${formData.length} total)`">
        <div style="overflow: auto" class="main-table-wrapper">
            <table class="modal-auto-tagging__table">
                <thead>
                    <tr>
                        <th>
                            <div class="input-checkbox">
                                <input id="modal-input-checkbox__all" type="checkbox" v-model="selectedAll">
                                <label for="modal-input-checkbox__all"></label>
                                <!-- <div>-</div> -->
                            </div>
                        </th>

                        <SortableColumnHeader
                            v-for="(column, index) in columns"
                            :key="index"
                            :column="column"
                            :sort="sort"
                            :class="{'wide-column': column.name === 'description'}"
                            @changeSort="changeSort"
                        />
                    </tr>
                </thead>

                <tbody>
                    <tr>
                        <td colspan="100%" class="modal-auto-tagging__table__top">
                            <div v-if="noUrlCount" class="modal-auto-tagging__label">
                                {{getNumberWithNoun('startup', noUrlCount)}} without URL
                            </div>
                        </td>
                    </tr>
                    <tr v-for="(row, rowIndex) in formData" :key="rowIndex">
                        <td>
                            <div class="input-checkbox">
                                <input :id="'modal-checkbox-' + rowIndex" type="checkbox" v-model="formData[rowIndex].selected">
                                <label :for="'modal-checkbox-' + rowIndex"></label>
                            </div>
                        </td>

                        <td v-for="(column, colIndex) in columns" :key="colIndex">
                            <a v-if="column.name === 'company' && row.startup_id" :href="`/startups/${row.startup_id}`" target="_blank">{{ row[column.name] }}</a>

                            <template v-else-if="column.name === 'url'">
                                <a v-if="row[column.name]" :href="row[column.name]" target="_blank">{{ row[column.name] }}</a>

                                <AutoTaggingUrl
                                    v-else
                                    v-model="formData[rowIndex][column.name]"
                                />
                            </template>

                            <AutoTaggingCustomValue
                                v-else-if="column.name === 'tagged_category'"
                                v-model="formData[rowIndex][column.name]"
                                :options="mainOptions"
                                :raw-value="formData[rowIndex][column.name + '_raw']"
                                @changed="mainOptionChanged(rowIndex)"
                            />

                            <AutoTaggingCustomValue
                                v-else-if="column.name === 'tagged_sub_category'"
                                v-model="formData[rowIndex][column.name]"
                                :options="getDependentOptions(row)"
                                :raw-value="formData[rowIndex][column.name + '_raw']"
                            />

                            <VClamp v-else
                                autoresize :max-lines="3"
                            >
                                {{ row[column.name] }}

                                <template #after="{ toggle, expanded, clamped }">
                                    <a class="link-normal" @click="toggle">
                                        <template v-if="clamped">
                                            more
                                        </template>
                                        <template v-if="expanded">
                                            less
                                        </template>
                                    </a>
                                </template>
                            </VClamp>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <template #modal-footer>
            <!-- <div style="display: flex; gap: 15px;"> -->
                <b-button @click="downloadFile" class="link link--download">Download CSV</b-button>
                <b-button variant="primary" @click="apply" class="btn-lg">Add Selected & Close</b-button>
            <!-- </div> -->
        </template>
    </b-modal>
</template>

<style scoped>
    .wide-column {
        min-width: 300px;
    }
</style>
